import type {
  CourseResponse,
  FullCurriculumCourse,
} from '@lms/api-client/backend';
import { Label } from '@lms/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lms/ui/select';
import { type FormEvent, useState } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import { addCourseToCurriculum } from '@/features/curriculums/curriculums-api';

// Ports app/[brand]/admin/curriculums/_components/add-course.tsx. The Next form
// used a hidden `order` field of `existingCourses.length + 1` and disabled the
// options for courses already in the curriculum; both are preserved. Revalidation
// happens via the caller's `onAdded` (SWR mutate) instead of `revalidatePath`.
export default function AddCourseToCurriculum({
  curriculumId,
  courses,
  existingCourses,
  onAdded,
}: {
  curriculumId: number;
  courses: CourseResponse[];
  existingCourses: FullCurriculumCourse[];
  onAdded: () => void;
}) {
  const locale = useLocale();
  const t = useTranslations('admin');
  const t_addcourse = useTranslations('addcourse');
  const [course, setCourse] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const order = existingCourses.length + 1;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!course) {
      return;
    }
    setError(undefined);
    setPending(true);
    try {
      const result = await addCourseToCurriculum(
        curriculumId,
        Number.parseInt(course, 10),
        order,
      );
      if (result.ok) {
        setCourse('');
        onAdded();
      } else {
        setError(result.error);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>{t('add_course_to_courseplan')}</Label>
        <Select
          value={course}
          onValueChange={setCourse}
          disabled={courses.length === 0}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                courses.length ? t('course') : t_addcourse('no_courses')
              }
            />
          </SelectTrigger>
          <SelectContent>
            {courses.map(({ id, data }) => (
              <SelectItem
                value={`${id}`}
                key={id}
                disabled={existingCourses.some((c) => c.id === id)}
              >
                {data[locale]?.name || data.en?.name || data[0]?.name || id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton
        label={t('add', { what: t('course').toLowerCase() })}
        pending={pending}
        disabled={!course}
      />
    </form>
  );
}
